import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Loader2, Sparkles, X } from 'lucide-react';
import { Button } from '../ui/button.tsx';
import { Card } from '../ui/card.tsx';
import { fetchParaClientFileEnabled } from '../../services/paraApi';
import { useClientState } from '../../services/clientStateService';
import useParaOpens from '../../hooks/useParaOpens';

// The window's code loads on first open: the shell stays light (AGENTS.md → Pages).
const ParaClientChat = lazy(() => import('./ParaClientChat'));

/**
 * The floating Para button (docs/plans/PARA_AGENT_PLAN.md Stage 4; owner, Oct 25 2026): beside the
 * support chat button, in its row (call window · Para · support). Shown only to people with Para access
 * in a firm with "Para on the client file" on (Admin → Client Allocation), and not on a client Para
 * doesn't open on (the Para trial). Hidden on phones, as the
 * support chat is, until the phone design is settled.
 */
export default function ParaButton({ user, enabled = true }) {
  const [available, setAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const { selectedClient } = useClientState();
  const paraOpens = useParaOpens(selectedClient?.id || null, { enabled: available });

  useEffect(() => {
    if (!enabled || !user?.email) {
      setAvailable(false);
      return undefined;
    }
    const controller = new AbortController();
    fetchParaClientFileEnabled({ signal: controller.signal })
      .then((on) => setAvailable(on))
      .catch(() => setAvailable(false));
    return () => controller.abort();
  }, [enabled, user?.email]);

  if (!available || paraOpens === false) return null;
  return (
    <div className="relative max-sm:hidden">
      {open && (
        <div className="absolute bottom-14 right-0 z-40">
          <Suspense
            fallback={(
              <Card className="flex h-[540px] w-[380px] items-center justify-center p-0 shadow-lg">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
              </Card>
            )}
          >
            <ParaClientChat user={user} onClose={() => setOpen(false)} />
          </Suspense>
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-12 w-12 rounded-full bg-card shadow-lg"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={open ? 'Close Para' : 'Ask Para about this client'}
        title="Para"
      >
        {open ? <X className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
      </Button>
    </div>
  );
}
